import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { LivespaceService } from '../../services/livespace.service';
import * as LivespaceActions from './livespace.actions';

@Injectable()
export class LivespaceEffects {
  connect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.livespaceConnect),
      switchMap(({ subdomain, api_key, api_secret }) =>
        this.livespaceService.connect(subdomain, api_key, api_secret).pipe(
          map((response) =>
            LivespaceActions.livespaceConnectSuccess({ user: response.data }),
          ),
          catchError((error) =>
            of(
              LivespaceActions.livespaceConnectFailure({
                error:
                  error?.error?.metadata?.message ||
                  error?.error?.message ||
                  'Connection failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  disconnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.livespaceDisconnect),
      switchMap(() =>
        this.livespaceService.disconnect().pipe(
          map((response) =>
            LivespaceActions.livespaceDisconnectSuccess({
              user: response.data,
            }),
          ),
          catchError((error) =>
            of(
              LivespaceActions.livespaceDisconnectFailure({
                error: error?.error?.metadata?.message || 'Disconnect failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.getLivespaceContact),
      switchMap(({ search_id }) =>
        this.livespaceService.showContact(search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('No contact found');
            }
            return LivespaceActions.getLivespaceContactSuccess({
              livespace_contact: response.data,
            });
          }),
          catchError((error) =>
            of(
              LivespaceActions.getLivespaceContactFailure({
                error: error?.message || 'Failed to get contact',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.getLivespaceCompanies),
      switchMap(({ search_id }) =>
        this.livespaceService.getCompanies(search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('No companies found');
            }
            return LivespaceActions.getLivespaceCompaniesSuccess({
              livespace_companies: response.data,
            });
          }),
          catchError((error) =>
            of(
              LivespaceActions.getLivespaceCompaniesFailure({
                error: error?.message || 'Failed to get companies',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getProcesses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.getLivespaceProcesses),
      switchMap(({ search_id }) =>
        this.livespaceService.getProcesses().pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('No processes found');
            }
            return LivespaceActions.getLivespaceProcessesSuccess({
              livespace_processes: response.data,
            });
          }),
          catchError((error) =>
            of(
              LivespaceActions.getLivespaceProcessesFailure({
                error: error?.message || 'Failed to get processes',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  newContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.livespaceNewContact),
      switchMap(
        ({
          user_id,
          first_name,
          last_name,
          email,
          phone,
          emails,
          phones,
          company_name,
          job_title,
          social_media,
          company_details,
        }) =>
          this.livespaceService
            .newContact(
              user_id,
              first_name,
              last_name,
              email,
              phone,
              company_name,
              job_title,
              emails,
              phones,
              social_media,
              company_details,
            )
            .pipe(
              map((response) =>
                LivespaceActions.livespaceNewContactSuccess({
                  livespace_new_contact: response.data,
                }),
              ),
              catchError((error) =>
                of(
                  LivespaceActions.livespaceNewContactFailure({
                    error:
                      error?.error?.metadata?.message ||
                      'Failed to create contact',
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  newCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.livespaceNewCompany),
      switchMap(
        ({
          user_id,
          company_name,
          company_domain,
          company_phone,
          company_city,
          company_state,
          company_email,
          company_linkedin,
          company_twitter,
          company_facebook,
          company_client_status,
        }) =>
          this.livespaceService
            .newCompany(
              user_id,
              company_name,
              company_domain,
              company_phone,
              company_city,
              company_state,
              company_email,
              company_linkedin,
              company_twitter,
              company_facebook,
              company_client_status,
            )
            .pipe(
              map((response) =>
                LivespaceActions.livespaceNewCompanySuccess({
                  livespace_new_company: response.data,
                }),
              ),
              catchError((error) =>
                of(
                  LivespaceActions.livespaceNewCompanyFailure({
                    error:
                      error?.error?.metadata?.message ||
                      'Failed to create company',
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  updateContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.livespaceUpdateContact),
      switchMap(
        ({
          user_id,
          contact_id,
          first_name,
          last_name,
          email,
          phone,
          emails,
          phones,
          company_name,
          job_title,
          social_media,
        }) =>
          this.livespaceService
            .updateContact(
              user_id,
              contact_id,
              first_name,
              last_name,
              email,
              phone,
              company_name,
              job_title,
              emails,
              phones,
              social_media,
            )
            .pipe(
              map((response) =>
                LivespaceActions.livespaceUpdateContactSuccess({
                  livespace_updated_contact: response.data,
                }),
              ),
              catchError((error) =>
                of(
                  LivespaceActions.livespaceUpdateContactFailure({
                    error:
                      error?.error?.metadata?.message ||
                      'Failed to update contact',
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.livespaceUpdateCompany),
      switchMap(
        ({
          user_id,
          company_id,
          company_name,
          company_domain,
          company_phone,
          company_city,
          company_state,
          company_email,
          company_linkedin,
          company_twitter,
          company_facebook,
        }) =>
          this.livespaceService
            .updateCompany(
              user_id,
              company_id,
              company_name,
              company_domain,
              company_phone,
              company_city,
              company_state,
              company_email,
              company_linkedin,
              company_twitter,
              company_facebook,
            )
            .pipe(
              map((response) =>
                LivespaceActions.livespaceUpdateCompanySuccess({
                  livespace_updated_company: response.data,
                }),
              ),
              catchError((error) =>
                of(
                  LivespaceActions.livespaceUpdateCompanyFailure({
                    error:
                      error?.error?.metadata?.message ||
                      'Failed to update company',
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  getDeals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.getLivespaceDeals),
      switchMap(({ contact_id, company_id }) =>
        this.livespaceService.getDeals(contact_id, company_id).pipe(
          map((response) =>
            LivespaceActions.getLivespaceDealsSuccess({
              livespace_deals: response.data,
            }),
          ),
          catchError((error) =>
            of(
              LivespaceActions.getLivespaceDealsFailure({
                error: error?.message || 'Failed to get deals',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  newDeal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.livespaceNewDeal),
      switchMap(
        ({
          user_id,
          deal_name,
          company_name,
          contact_name,
          process_id,
          date_end,
          source_id,
          owner_id,
          visibility,
          category_name,
          product_id,
          price,
        }) =>
          this.livespaceService
            .newDeal(
              user_id,
              deal_name,
              company_name,
              contact_name,
              process_id,
              date_end,
              source_id,
              owner_id,
              visibility,
              category_name,
              product_id,
              price,
            )
            .pipe(
              map((response) =>
                LivespaceActions.livespaceNewDealSuccess({
                  livespace_new_deal: response.data,
                }),
              ),
              catchError((error) =>
                of(
                  LivespaceActions.livespaceNewDealFailure({
                    error:
                      error?.error?.metadata?.message ||
                      'Failed to create deal',
                  }),
                ),
              ),
            ),
      ),
    ),
  );

  getSources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.getLivespaceSources),
      switchMap(() =>
        this.livespaceService.getSources().pipe(
          map((response) =>
            LivespaceActions.getLivespaceSourcesSuccess({
              livespace_sources: response.data,
            }),
          ),
          catchError((error) =>
            of(
              LivespaceActions.getLivespaceSourcesFailure({
                error: error?.message || 'Failed to get sources',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.getLivespaceUsers),
      switchMap(() =>
        this.livespaceService.getUsers().pipe(
          map((response) =>
            LivespaceActions.getLivespaceUsersSuccess({
              livespace_users: response.data,
            }),
          ),
          catchError((error) =>
            of(
              LivespaceActions.getLivespaceUsersFailure({
                error: error?.message || 'Failed to get users',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.getLivespaceProducts),
      switchMap(() =>
        this.livespaceService.getProducts().pipe(
          map((response) =>
            LivespaceActions.getLivespaceProductsSuccess({
              livespace_products: response.data,
            }),
          ),
          catchError((error) =>
            of(
              LivespaceActions.getLivespaceProductsFailure({
                error: error?.message || 'Failed to get products',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LivespaceActions.getLivespaceCurrentUser),
      switchMap(() =>
        this.livespaceService.getCurrentUser().pipe(
          map((response) =>
            LivespaceActions.getLivespaceCurrentUserSuccess({
              livespace_current_user: response.data,
            }),
          ),
          catchError((error) =>
            of(
              LivespaceActions.getLivespaceCurrentUserFailure({
                error: error?.message || 'Failed to get current user',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private livespaceService: LivespaceService,
  ) {}
}
